import format from 'date-fns/format';
import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';
import {
  XYPlot,
  LineSeries,
  XAxis,
  YAxis,
  DiscreteColorLegend,
  HorizontalGridLines,
  MarkSeries,
} from 'react-vis';
import common from 'constants/common';
import BaseLoader from 'components/atoms/base-loader';
import { useAppSelector } from 'store';

import ChartHint from './chartHint';
import StickySpO2YAxis from './spO2StickyYAxis';

const legendItems = [
  {
    title: 'SpO2',
    color: common.chartColors.spO2.main,
    strokeStyle: 'solid',
  },
  {
    title: 'SpO2 High',
    color: common.chartColors.spO2.high,
    strokeStyle: 'dashed',
  },
];

const normalizeChartData = (data, thresholds, dates) => {
  const deviceData = data;
  const [startTime, endTime] = dates;
  let spO2Data = deviceData.map((item) => ({
    value: item.data.spo2,
    createdAt: item.createdAt,
  }));
  if (spO2Data.length < 1) {
    const emptyData = [
      {
        value: null,
        createdAt: startTime.getTime(),
      },
      {
        value: null,
        createdAt: endTime.getTime(),
      },
    ];
    spO2Data = emptyData;
  }
  const spO2High = spO2Data.map((item) => ({
    x: item.createdAt,
    y: thresholds.riskBloodoxygen,
  }));
  const spO2Main = spO2Data.map((item) => ({
    x: item.createdAt,
    y: item.value,
  }));
  if (spO2High.length === 1) {
    const xValue = spO2High[0].x;
    spO2High.unshift({
      x: xValue - 86400000,
      y: thresholds.riskBloodoxygen,
    });
    spO2High.push({
      x: parseInt(xValue) + 86400000,
      y: thresholds.riskBloodoxygen,
    });
  }
  return {
    spO2: {
      high: {
        data: spO2High,
        stroke: common.chartColors.spO2.high,
        strokeStyle: 'dashed',
      },
      main: {
        data: spO2Main,
        stroke: common.chartColors.spO2.main,
        strokeStyle: 'solid',
      },
    },
  };
};

const SpO2 = ({ data, thresholds, loading }) => {
  const dates = useAppSelector((store) => store.patient.dates);
  const isHoveringOverLine = useRef({});
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const [hintType, setHintType] = useState('');
  const yScale = common.chartYaxisScales.spO2;
  let chartDate = '';
  if (loading) return <BaseLoader className="loader" loading />;
  const chartData = normalizeChartData(data, thresholds, dates);

  return (
    <div className="chart" data-cy="spo2-chart">
      <div className="chart-header">
        <div className="chart-header-title" data-cy="spo2-chart-title">
          <p>SpO2</p>
          <p>Oxygen saturation O2%</p>
        </div>
        <DiscreteColorLegend items={legendItems} orientation="horizontal" />
      </div>
      <div className="chart-main">
        <StickySpO2YAxis />
        <XYPlot
          xType="time"
          height={common.chartHeight}
          onMouseLeave={() => {
            setHoveredPoint(null);
          }}
          yDomain={yScale}
          width={common.chartWidth}
        >
          <HorizontalGridLines tickTotal={11} />
          <XAxis
            attr="x"
            attrAxis="y"
            orientation="bottom"
            tickFormat={(d) => {
              const dayMonth = format(d, 'MMM-dd');
              if (dayMonth !== chartDate) {
                chartDate = dayMonth;
                return format(d, common.dateFormats.chartXDate);
              }
              return '';
            }}
            tickSizeOuter={5}
            tickSizeInner={0}
            tickPadding={24}
          />
          <XAxis
            attr="x"
            attrAxis="y"
            orientation="bottom"
            tickFormat={(d) => format(d, common.dateFormats.chartXTime)}
            tickTotal={15}
            tickSizeOuter={5}
            tickSizeInner={0}
          />
          <YAxis
            attr="y"
            attrAxis="x"
            orientation="left"
            tickTotal={11}
            className="hidden-axis"
          />
          <LineSeries curve={null} opacity={1} {...chartData.spO2.high} />
          {chartData.spO2.main.data[0].y !== null && data.length === 1 && (
            <MarkSeries
              color={common.chartColors.spO2.main}
              data={chartData.spO2.main.data}
              opacity={1}
              size={2.5}
              onNearestXY={(datapoint) => {
                setHoveredPoint(datapoint);
                setHintType('SpO2');
              }}
            />
          )}
          {chartData.spO2.main.data[0].y !== null && data.length > 1 && (
            <LineSeries
              curve={null}
              opacity={1}
              {...chartData.spO2.main}
              onSeriesMouseOver={() => {
                isHoveringOverLine.current[0] = true;
              }}
              onSeriesMouseOut={() => {
                isHoveringOverLine.current[0] = false;
              }}
              onNearestXY={(datapoint) => {
                if (isHoveringOverLine.current[0]) {
                  setHoveredPoint(datapoint);
                  setHintType('SpO2');
                }
              }}
            />
          )}
          {hoveredPoint && (
            <ChartHint hoveredPoint={hoveredPoint} type={hintType} />
          )}
        </XYPlot>
      </div>
    </div>
  );
};

SpO2.propTypes = {
  data: PropTypes.array,
  thresholds: PropTypes.shape(),
  loading: PropTypes.bool,
};

export default SpO2;
